import { cn } from "@/lib/utils";
import tadpoleImage from "@/assets/tadpole.png";
import youngFrogImage from "@/assets/young-frog.png";
import frogImage from "@/assets/frog.png";

type FrogStage = "egg" | "tadpole" | "young" | "adult" | "hibernating";

interface FrogAvatarProps {
  stage: FrogStage;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  daysCompleted?: number;
}

const stageConfig = {
  egg: {
    emoji: "🥚",
    image: null,
    bgColor: "bg-frog-egg",
    label: "Ovo",
    daysNeeded: 3,
  },
  tadpole: {
    emoji: null,
    image: tadpoleImage,
    bgColor: "bg-frog-tadpole",
    label: "Girino",
    daysNeeded: 10,
  },
  young: {
    emoji: null,
    image: youngFrogImage,
    bgColor: "bg-frog-young",
    label: "Jovem",
    daysNeeded: 31,
  },
  adult: {
    emoji: null,
    image: frogImage,
    bgColor: "bg-frog-adult",
    label: "Adulto",
    daysNeeded: 31,
  },
  hibernating: {
    emoji: "💤",
    image: null,
    bgColor: "bg-frog-hibernate",
    label: "Hibernando",
    daysNeeded: 0,
  },
};

const sizeConfig = {
  sm: "w-10 h-10 text-lg",
  md: "w-14 h-14 text-2xl",
  lg: "w-20 h-20 text-3xl",
  xl: "w-28 h-28 text-5xl",
};

const animationConfig: Record<FrogStage, string> = {
  egg: "animate-wobble",
  tadpole: "animate-swim",
  young: "animate-float",
  adult: "animate-float",
  hibernating: "",
};

export const FrogAvatar = ({
  stage,
  size = "md",
  className,
  daysCompleted = 0,
}: FrogAvatarProps) => {
  const config = stageConfig[stage];

  return (
    <div className={cn("relative group", className)}>
      <div
        className={cn(
          "rounded-full flex items-center justify-center shadow-float transition-all duration-300",
          config.bgColor,
          sizeConfig[size],
          animationConfig[stage],
          "hover:scale-110"
        )}
      >
        {config.image ? (
          <img src={config.image} alt={config.label} className="w-3/4 h-3/4 object-contain drop-shadow-md" />
        ) : (
          <span className="drop-shadow-md">{config.emoji}</span>
        )}
      </div>
      
      {/* Progress ring for eggs */}
      {stage === "egg" && daysCompleted < 3 && (
        <svg
          className="absolute inset-0 -rotate-90"
          viewBox="0 0 100 100"
        >
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            className="text-muted/30"
          />
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            className="text-coral"
            strokeDasharray={`${(daysCompleted / 3) * 283} 283`}
            strokeLinecap="round"
          />
        </svg>
      )}

      {/* Tooltip on hover */}
      <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
        <div className="bg-foreground text-background text-xs px-2 py-1 rounded-lg whitespace-nowrap">
          {config.label}
        </div>
      </div>
    </div>
  );
};

export const getNextStage = (currentStage: FrogStage, daysCompleted: number): FrogStage => {
  if (currentStage === "egg" && daysCompleted >= 3) return "tadpole";
  if (currentStage === "tadpole" && daysCompleted >= 10) return "young";
  if (currentStage === "young" && daysCompleted >= 31) return "adult";
  return currentStage;
};

export const getStageDaysRequired = (stage: FrogStage): number => {
  return stageConfig[stage].daysNeeded;
};
